type TokenKind = 'year' | 'month' | 'day';
type MaskSlot = { kind: TokenKind; width: number };

type MaskShape = {
  slots: MaskSlot[];
  literals: string[];
  totalWidth: number;
};

export type DateFieldState = {
  digits: string;
  display: string;
  iso: string;
  reported: string;
};

export type DateFieldModel = {
  readonly placeholder: string;
  readonly empty: DateFieldState;
  accept: (previous: DateFieldState, rawText: string) => DateFieldState;
  fromStored: (value: string) => DateFieldState;
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DECIMAL_DIGIT = /\p{Nd}/u;
const FALLBACK_MASK = 'MM/DD/YYYY';
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const SLOT_PLACEHOLDER: Record<TokenKind, string> = {
  year: 'YYYY',
  month: 'MM',
  day: 'DD',
};

/**
 * Calendar-validates a strict ISO 8601 date without using `Date`.
 *
 * @param value - Candidate `YYYY-MM-DD` string.
 * @returns `true` when the calendar date exists.
 */
export function isValidIsoCalendarDate(value: string): boolean {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12) return false;

  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = month === 2 && leap ? 29 : DAYS_IN_MONTH[month - 1];
  return day >= 1 && day <= daysInMonth;
}

function toAsciiDigits(value: string): string {
  let digits = '';

  for (const char of value) {
    if (!DECIMAL_DIGIT.test(char)) continue;
    digits += String(decimalDigitValue(char));
  }

  return digits;
}

function decimalDigitValue(char: string): number {
  const codePoint = char.codePointAt(0);
  if (codePoint === undefined) return 0;

  for (let offset = 0; offset <= 9; offset += 1) {
    const zeroCodePoint = codePoint - offset;
    if (zeroCodePoint < 1) return offset;

    if (!DECIMAL_DIGIT.test(String.fromCodePoint(zeroCodePoint - 1))) {
      return offset;
    }
  }

  return 0;
}

function matchToken(
  rest: string
): { kind: TokenKind; width: number; consumed: number } | null {
  if (rest.startsWith('YYYY')) return { kind: 'year', width: 4, consumed: 4 };
  if (rest.startsWith('MM')) return { kind: 'month', width: 2, consumed: 2 };
  if (rest.startsWith('DD')) return { kind: 'day', width: 2, consumed: 2 };
  if (rest.startsWith('M')) return { kind: 'month', width: 2, consumed: 1 };
  if (rest.startsWith('D')) return { kind: 'day', width: 2, consumed: 1 };
  return null;
}

function parseMask(source: string): MaskShape {
  const slots: MaskSlot[] = [];
  const literals: string[] = [];
  let literal = '';
  let index = 0;

  while (index < source.length) {
    const token = matchToken(source.slice(index));
    if (!token) {
      literal += source[index];
      index += 1;
      continue;
    }
    literals.push(literal);
    literal = '';
    slots.push({ kind: token.kind, width: token.width });
    index += token.consumed;
  }
  literals.push(literal);

  return {
    slots,
    literals,
    totalWidth: slots.reduce((sum, slot) => sum + slot.width, 0),
  };
}

function buildState(shape: MaskShape, digits: string): DateFieldState {
  let display = '';
  let cursor = 0;

  for (let slotIndex = 0; slotIndex < shape.slots.length; slotIndex += 1) {
    if (cursor >= digits.length) break;
    display += shape.literals[slotIndex] ?? '';
    const taken = digits.slice(cursor, cursor + shape.slots[slotIndex].width);
    display += taken;
    cursor += taken.length;
  }

  const isComplete = digits.length === shape.totalWidth;
  if (isComplete) display += shape.literals[shape.slots.length] ?? '';

  const parts: Record<TokenKind, string> = { year: '', month: '', day: '' };
  if (isComplete) {
    let offset = 0;
    shape.slots.forEach((slot) => {
      parts[slot.kind] = digits.slice(offset, offset + slot.width);
      offset += slot.width;
    });
  }

  const iso = isComplete ? `${parts.year}-${parts.month}-${parts.day}` : '';
  return { digits, display, iso, reported: isComplete ? iso : display };
}

/**
 * Creates a date-field reducer for a locale mask.
 *
 * @param mask - Locale `L` mask such as `MM/DD/YYYY`.
 * @returns Model with `accept` and `fromStored`.
 */
export function createDateFieldModel(mask: string): DateFieldModel {
  const shape = parseMask(mask);
  const empty: DateFieldState = {
    digits: '',
    display: '',
    iso: '',
    reported: '',
  };
  const placeholder =
    shape.slots
      .map(
        (slot, slotIndex) =>
          `${shape.literals[slotIndex] ?? ''}${SLOT_PLACEHOLDER[slot.kind]}`
      )
      .join('') + (shape.literals[shape.slots.length] ?? '');

  const accept = (
    previous: DateFieldState,
    rawText: string
  ): DateFieldState => {
    let digits = toAsciiDigits(rawText);
    if (
      digits === previous.digits &&
      rawText.length < previous.display.length
    ) {
      digits = digits.slice(0, -1);
    }
    return buildState(shape, digits.slice(0, shape.totalWidth));
  };

  const fromStored = (value: string): DateFieldState => {
    if (!value) return empty;
    const isoMatch = ISO_DATE_PATTERN.exec(value);
    if (isoMatch) {
      const byKind: Record<TokenKind, string> = {
        year: isoMatch[1],
        month: isoMatch[2],
        day: isoMatch[3],
      };
      return buildState(
        shape,
        shape.slots.map((slot) => byKind[slot.kind]).join('')
      );
    }
    return accept(empty, value);
  };

  return { placeholder, empty, accept, fromStored };
}

/**
 * Builds a numeric date mask from the runtime locale (e.g. `MM/DD/YYYY`).
 *
 * @param language - UI language code.
 * @returns Mask string with `YYYY`, `MM`, and `DD` slots.
 */
export function resolveDateMask(language: string): string {
  const code = language.trim().replace(/_/g, '-') || 'en';

  try {
    const parts = new Intl.DateTimeFormat(code, {
      calendar: 'gregory',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(2024, 4, 30));

    const mask = parts
      .map((part) => {
        if (part.type === 'year') return 'YYYY';
        if (part.type === 'month') return 'MM';
        if (part.type === 'day') return 'DD';
        if (part.type === 'literal') return part.value;
        return '';
      })
      .join('');

    if (mask.includes('YYYY') && mask.includes('MM') && mask.includes('DD')) {
      return mask;
    }
  } catch {
    // unregistered locales fall back to US order
  }

  return FALLBACK_MASK;
}
